import { ROUTE, axiosInstance } from './axiosInstance';

import type {
  MediaFilterCatalog,
  AddMediaSearchHistoryParams,
  DeleteMediaSearchHistoryParams,
  CreateMediaFavouriteCategoryParams,
  CreateMediaFavouriteShareParams,
  DeleteMediaFavouriteCategoryParams,
  DeleteMediaFavouriteShareParams,
  GetMediaFavouritesParams,
  GetMediaKeywordTimeseriesParams,
  GetMediaKeywordTimeseriesResult,
  GetMediaParams,
  GetMediaResult,
  MediaDateTimeRange,
  MediaFavouriteCategory,
  MediaFavouritePost,
  MediaFavouriteShare,
  ReorderMediaFavouritesParams,
  SharedFavouriteTab,
  UpdateMediaFavouriteCategoryParams,
  UpdateMediaFavouriteParams,
} from '@/server/services/GoogleBigQuery/BigQueryMediaClient.types';
import type { ExportPdfParams } from '@/app/api/search-console/export-pdf/types';

/** Scalar params serialized individually in getExportPdfUrl; all other string[] keys are filter params. */
const RESERVED_PDF_SCALAR_KEYS = new Set([
  'favourites', 'favouriteTab', 'translated', 'search',
  'dateFrom', 'dateTo', 'orderBy', 'orderSort', 'format',
]);

export const media = {
  list: async (params: GetMediaParams = {}) => {
    const { data } = await axiosInstance.get<GetMediaResult>(ROUTE.SEARCH_CONSOLE.LIST, { params });
    return data;
  },
  getKeywordTimeseries: async (params: GetMediaKeywordTimeseriesParams) => {
    const { data } = await axiosInstance.get<GetMediaKeywordTimeseriesResult>(
      ROUTE.SEARCH_CONSOLE.KEYWORD_TIMESERIES,
      { params },
    );
    return data;
  },
  getDateTimeRange: async () => {
    const { data } = await axiosInstance.get<MediaDateTimeRange>(ROUTE.SEARCH_CONSOLE.DATE_TIME_RANGE);
    return data;
  },
  getAlignmentFilters: async () => {
    const { data } = await axiosInstance.get<MediaFilterCatalog>(ROUTE.SEARCH_CONSOLE.ALIGNMENT_FILTERS);
    return data;
  },
  listFavourites: async (params: GetMediaFavouritesParams = {}) => {
    const { data } = await axiosInstance.get<MediaFavouritePost[]>(ROUTE.SEARCH_CONSOLE.FAVOURITES, { params });
    return data;
  },
  updateFavourite: async (params: UpdateMediaFavouriteParams) => {
    const { data } = await axiosInstance.post<MediaFavouritePost | null>(ROUTE.SEARCH_CONSOLE.FAVOURITES, params);
    return data;
  },
  reorderFavourites: async (params: ReorderMediaFavouritesParams) => {
    const { data } = await axiosInstance.patch<{ ok: true }>(ROUTE.SEARCH_CONSOLE.FAVOURITES, params);
    return data;
  },
  listFavouriteCategories: async () => {
    const { data } = await axiosInstance.get<MediaFavouriteCategory[]>(ROUTE.SEARCH_CONSOLE.FAVOURITE_CATEGORIES);
    return data;
  },
  createFavouriteCategory: async (params: CreateMediaFavouriteCategoryParams) => {
    const { data } = await axiosInstance.post<MediaFavouriteCategory>(
      ROUTE.SEARCH_CONSOLE.FAVOURITE_CATEGORIES,
      params,
    );
    return data;
  },
  updateFavouriteCategory: async (params: UpdateMediaFavouriteCategoryParams) => {
    const { data } = await axiosInstance.patch<MediaFavouriteCategory | null>(
      ROUTE.SEARCH_CONSOLE.FAVOURITE_CATEGORIES,
      params,
    );
    return data;
  },
  deleteFavouriteCategory: async (params: DeleteMediaFavouriteCategoryParams) => {
    const { data } = await axiosInstance.delete<{ id: number }>(ROUTE.SEARCH_CONSOLE.FAVOURITE_CATEGORIES, {
      data: params,
    });
    return data;
  },
  listFavouriteShares: async () => {
    const { data } = await axiosInstance.get<MediaFavouriteShare[]>(ROUTE.SEARCH_CONSOLE.FAVOURITE_SHARES);
    return data;
  },
  createFavouriteShare: async (params: CreateMediaFavouriteShareParams) => {
    const { data } = await axiosInstance.post<MediaFavouriteShare>(ROUTE.SEARCH_CONSOLE.FAVOURITE_SHARES, params);
    return data;
  },
  deleteFavouriteShare: async (params: DeleteMediaFavouriteShareParams) => {
    const { data } = await axiosInstance.delete<{ categoryId: number }>(ROUTE.SEARCH_CONSOLE.FAVOURITE_SHARES, {
      data: params,
    });
    return data;
  },
  listSharedFavourites: async () => {
    const { data } = await axiosInstance.get<SharedFavouriteTab[]>(ROUTE.SEARCH_CONSOLE.SHARED_FAVOURITES);
    return data;
  },
  listSearchHistory: async () => {
    const { data } = await axiosInstance.get<string[]>(ROUTE.SEARCH_CONSOLE.SEARCH_HISTORY);
    return data;
  },
  addSearchHistory: async (params: AddMediaSearchHistoryParams) => {
    const { data } = await axiosInstance.post<string[]>(ROUTE.SEARCH_CONSOLE.SEARCH_HISTORY, params);
    return data;
  },
  deleteSearchHistory: async (params: DeleteMediaSearchHistoryParams = {}) => {
    const { data } = await axiosInstance.delete<string[]>(ROUTE.SEARCH_CONSOLE.SEARCH_HISTORY, { data: params });
    return data;
  },
  /**
   * Builds the URL for the streaming PDF / CSV export endpoint. Used for a native
   * browser download (anchor navigation) so the browser writes bytes straight
   * to disk — the page never holds the (potentially huge) dataset in memory.
   */
  getExportPdfUrl: (params: ExportPdfParams = {}): string => {
    const query = new URLSearchParams();
    if (params.favourites) query.set('favourites', 'true');
    if (params.favouriteTab) query.set('favouriteTab', params.favouriteTab);
    if (params.translated) query.set('translate', 'true');
    if (params.search) query.set('search', params.search);
    if (params.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params.dateTo) query.set('dateTo', params.dateTo);
    if (params.orderBy) query.set('orderBy', params.orderBy);
    if (params.orderSort) query.set('orderSort', params.orderSort);
    if (params.format) query.set('format', params.format);

    for (const [key, value] of Object.entries(params)) {
      if (RESERVED_PDF_SCALAR_KEYS.has(key)) continue;

      if (!Array.isArray(value)) continue;

      (value as unknown[]).forEach((v) => {
        if (typeof v === 'string') query.append(`${key}[]`, v);
      });
    }

    const qs = query.toString();
    return `/api${ROUTE.SEARCH_CONSOLE.EXPORT_PDF}${qs ? `?${qs}` : ''}`;
  },
};
